import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { X, Bell, Mail, Smartphone, Filter, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

interface WhaleSettingsProps {
  onClose: () => void;
}

export function WhaleSettings({ onClose }: WhaleSettingsProps) {
  const [settings, setSettings] = useState({
    minTransactionSize: 100000,
    notifications: {
      email: true,
      push: true,
      inApp: true,
    },
    alertTypes: {
      large_transfer: true,
      exchange_flow: true,
      accumulation_pattern: true,
      distribution_pattern: true,
      tracked_wallet_activity: true,
      unusual_activity: true,
    },
    significanceThreshold: 'medium' as 'low' | 'medium' | 'high' | 'critical',
  });

  const handleSave = () => {
    console.log('Saving settings:', settings);
    toast.success('Configuracion guardada');
    onClose();
  };

  const thresholdLabels = {
    low: '$100K+',
    medium: '$500K+',
    high: '$1M+',
    critical: '$10M+',
  };

  const thresholdIndex = ['low', 'medium', 'high', 'critical'].indexOf(settings.significanceThreshold);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Configuracion de Alertas</CardTitle>
            <CardDescription>Personaliza que alertas quieres recibir</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Minimum Transaction Size */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <Label className="text-base font-medium">Tamano minimo de transaccion</Label>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Solo alertas para transacciones mayores a:
                </span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  ${settings.minTransactionSize.toLocaleString()}
                </span>
              </div>
              <Slider
                value={[settings.minTransactionSize]}
                onValueChange={([value]) => setSettings({ ...settings, minTransactionSize: value })}
                min={10000}
                max={10000000}
                step={10000}
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>$10K</span>
                <span>$10M</span>
              </div>
            </div>
          </div>

          {/* Significance Threshold */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-violet-500" />
              <Label className="text-base font-medium">Umbral de importancia</Label>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Recibir alertas de importancia {thresholdLabels[settings.significanceThreshold]} o superior
              </p>
              <Slider
                value={[thresholdIndex]}
                onValueChange={([index]) => {
                  const thresholds: ('low' | 'medium' | 'high' | 'critical')[] = ['low', 'medium', 'high', 'critical'];
                  setSettings({ ...settings, significanceThreshold: thresholds[index] });
                }}
                min={0}
                max={3}
                step={1}
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>Bajo</span>
                <span>Medio</span>
                <span>Alto</span>
                <span>Critico</span>
              </div>
            </div>
          </div>

          {/* Notification Methods */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-500" />
              <Label className="text-base font-medium">Metodos de notificacion</Label>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">Push Notifications</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Alertas en tiempo real en tu dispositivo</p>
                  </div>
                </div>
                <Switch
                  checked={settings.notifications.push}
                  onCheckedChange={(checked) =>
                    setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, push: checked },
                    })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">Email</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Resumen diario por correo</p>
                  </div>
                </div>
                <Switch
                  checked={settings.notifications.email}
                  onCheckedChange={(checked) =>
                    setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, email: checked },
                    })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">In-App</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Notificaciones dentro de la app</p>
                  </div>
                </div>
                <Switch
                  checked={settings.notifications.inApp}
                  onCheckedChange={(checked) =>
                    setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, inApp: checked },
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Alert Types */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Tipos de alerta</Label>
            <div className="space-y-2">
              {[
                { key: 'large_transfer', label: 'Transferencias grandes', desc: 'Movimientos de grandes cantidades' },
                { key: 'exchange_flow', label: 'Flujo de exchanges', desc: 'Depositos y retiros de exchanges' },
                { key: 'accumulation_pattern', label: 'Patrones de acumulacion', desc: 'Deteccion de acumulacion whale' },
                { key: 'distribution_pattern', label: 'Patrones de distribucion', desc: 'Deteccion de distribucion/venta' },
                { key: 'tracked_wallet_activity', label: 'Wallets rastreadas', desc: 'Actividad de tus wallets seguidas' },
                { key: 'unusual_activity', label: 'Actividad inusual', desc: 'Comportamiento anomalo detectado' },
              ].map(({ key, label, desc }) => (
                <div
                  key={key}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{label}</p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">{desc}</p>
                  </div>
                  <Switch
                    checked={settings.alertTypes[key as keyof typeof settings.alertTypes]}
                    onCheckedChange={(checked) =>
                      setSettings({
                        ...settings,
                        alertTypes: { ...settings.alertTypes, [key]: checked },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              onClick={handleSave}
            >
              Guardar Configuracion
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
